import { storeCurrentLocationAsPostLoginReturnUrl } from '../../../../routing/infrastructure/shellPostLoginReturnUrl.ts';
import type { ShellAuthApplicationPort } from '../../../../app-context/domain/ports/ShellAuthApplicationPort.ts';
import type { ShellNavigationPort } from '../../../../app-context/domain/ports/ShellNavigationPort.ts';
import { resolveShellPermissions } from '../../../../app-context/domain/shellModulePermissions.ts';
import type { PresentationShellPort } from '../../../../presentation/application/ports/PresentationShellPort.ts';
import type { CoreShellPorts } from '../../../../routing/domain/CoreShellPorts.ts';
/** Require auth. */


export function requireAuth(
  auth: ShellAuthApplicationPort,
  navigation: ShellNavigationPort,
  handler: () => void | Promise<void>,
): () => Promise<void> {
  return async () => {
    const isAuth = await auth.isAuthenticated();
    if (!isAuth) {
      storeCurrentLocationAsPostLoginReturnUrl();
      navigation.navigate('/login');
      return;
    }
    await handler();
  };
}
/** Require auth and shell permission. */


export function requireAuthAndShellPermission<T extends CoreShellPorts>(
  auth: ShellAuthApplicationPort,
  navigation: ShellNavigationPort,
  permission: string,
  shellPorts: T,
  handler: () => void | Promise<void>,
): () => Promise<void> {
  return requireAuth(auth, navigation, async () => {
    let user: Awaited<ReturnType<ShellAuthApplicationPort['getCurrentUser']>>;
    try {
      user = await auth.getCurrentUser();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      shellPorts.telemetry.logError(msg, { source: 'shell.route.permission', permission, error: e });
      shellPorts.notifications.error('No se pudo comprobar el usuario.');
      navigation.navigate('/login');
      return;
    }
    const perms = resolveShellPermissions(user);
    if (!perms.has(permission)) {
      navigation.navigate('/');
      return;
    }
    await handler();
  });
}

/** Typical feature route: session + RBAC permission + page bundle import + mount in `#main`. */
export function createShellPermissionMainPageRoute<T extends CoreShellPorts>(
  auth: ShellAuthApplicationPort,
  navigation: ShellNavigationPort,
  presentation: PresentationShellPort<T>,
  permission: string,
  pageId: string,
  loadPage: () => Promise<unknown>,
  shellPorts: T,
): () => Promise<void> {
  return requireAuthAndShellPermission(auth, navigation, permission, shellPorts, async () => {
    try {
      await loadPage();
      await presentation.createMainPageHandler(pageId, shellPorts)();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      shellPorts.telemetry.logError(msg, { source: 'shell.route.page', pageId, error: e });
      shellPorts.notifications.error('No se pudo cargar esta sección.');
      navigation.navigate('/');
    }
  });
}
